import { createApiClient, createFetchTransport } from '@carbonwebmail/api-client'
import { ClerkProvider, useAuth } from '@clerk/react'
import { StrictMode, useMemo } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './app/App.js'
import './styles.css'

/**
 * The browser's composition root, and the only file here that constructs the
 * client.
 *
 * Requests are relative, because the API and this app share an origin — and
 * that is structural rather than assumed. In production the API service serves
 * this bundle itself; in development Vite proxies `/api` and `/u` to it.
 *
 * That assumption was wrong once, and the failure was silent: the app was on a
 * static host that answered every path with 200 for the SPA fallback, so an
 * unsubscribe POST hit the static host, came back 200, and the page told people
 * they had been unsubscribed while nothing was recorded. The fix then was an
 * explicit origin; the fix now is that there is only one.
 *
 * **Identity is Clerk's and the tenant is ours** (ADR 0004). `ClerkProvider`
 * owns signing in, signing out and the session token; every request this app
 * makes carries that token, and the server turns it into an organisation and a
 * role from its own tables.
 */
const root = document.querySelector('#root')
if (root === null) throw new Error('index.html is missing its #root element')

/**
 * Read at build time, and absent is fatal rather than degraded.
 *
 * Without it `ClerkProvider` cannot start, so nobody can sign in — and a build
 * that renders a sign-in screen which can never work is worse than one that
 * refuses to start. The key is public by design: it names the instance and
 * authorises nothing.
 */
const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
if (publishableKey === undefined || publishableKey === '') {
  throw new Error('VITE_CLERK_PUBLISHABLE_KEY was not set when this bundle was built')
}

/**
 * The client, built where the token can be read.
 *
 * `getToken` is asked once per request rather than read once here: the session
 * token lives about a minute and is renewed in the background, so a captured
 * one stops authenticating almost immediately — which presents as being signed
 * out at random.
 */
function CarbonMail() {
  const { getToken } = useAuth()

  const client = useMemo(
    () =>
      createApiClient({
        transport: createFetchTransport(async () => (await getToken()) ?? undefined),
      }),
    [getToken],
  )

  return <App client={client} />
}

createRoot(root).render(
  <StrictMode>
    <ClerkProvider publishableKey={publishableKey} afterSignOutUrl="/signin">
      <CarbonMail />
    </ClerkProvider>
  </StrictMode>,
)
